/**
 * Product Customization Form Handler
 * Gestiona la personalización de productos usando Product Bundling de Shopify
 * Basado en el sistema de valencia-basket-horizon adaptado para PadelProShop
 */

class ProductCustomizationFormHandler {
  constructor() {
    this.form = null;
    this.variantIdInput = null;
    this.parentIdInputs = [];
    this.addonInputsByIndex = new Map();
    this.togglesByIndex = new Map();
    this.textInputsByIndex = new Map();
    this.addonMetaByIndex = new Map();
    this.sharedPid = null;
    this.basePidInput = null;
    this.baseCustomizedInput = null;
    this.baseLinkedItemInput = null;

    this.init();
  }

  /**
   * Inicializa el handler
   */
  init() {
    // Esperar a que el DOM esté listo
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.setup());
    } else {
      this.setup();
    }
  }

  /**
   * Configura todos los elementos y event listeners
   */
  setup() {
    // Encontrar el formulario principal del producto
    this.form = this.resolveForm();
    if (!this.form) {
      return;
    }

    // Encontrar el input del variant ID principal
    this.variantIdInput = this.form.querySelector('input[name="id"]');
    if (!this.variantIdInput) {
      return;
    }

    // IMPORTANTE: Crear los inputs dinámicamente DENTRO del formulario
    this.createAddonInputs();

    // Recopilar todos los toggles (checkboxes)
    const toggles = document.querySelectorAll(".personalization-toggle");
    toggles.forEach((toggle) => {
      const addonIndex = toggle.dataset.addonIndex;
      this.togglesByIndex.set(addonIndex, toggle);

      // Event listener para el toggle
      toggle.addEventListener("change", (e) => this.handleToggleChange(e));
    });

    // Recopilar inputs de texto
    const textInputs = document.querySelectorAll(".customization-text-input");
    textInputs.forEach((input) => {
      const addonIndex = input.dataset.addonIndex;
      this.textInputsByIndex.set(addonIndex, input);

      // Event listener para sincronizar el texto con el property hidden
      input.addEventListener("input", (e) => this.handleTextInputChange(e));
    });

    // Sincronizar estado inicial (por si algún toggle viene marcado).
    this.togglesByIndex.forEach((toggle, addonIndex) => {
      if (toggle.checked) {
        this.enableAddonInputs(addonIndex);
        this.showTextInput(addonIndex);
      } else {
        this.disableAddonInputs(addonIndex);
        this.hideTextInput(addonIndex);
      }
    });

    // Sincronizar parent_id inicial
    this.updateAllParentIds();

    // Listener para cambios de variante
    this.variantIdInput.addEventListener("change", () =>
      this.updateAllParentIds()
    );

    // También escuchar el evento custom de cambio de variante (si existe)
    document.addEventListener("variant:change", () =>
      this.updateAllParentIds()
    );
  }

  /**
   * Resuelve el formulario de producto asociado al bloque de personalización.
   * Prioriza el formulario dentro de la misma sección para evitar enganchar otro form.
   */
  resolveForm() {
    const personalizationContainer = document.querySelector(
      "[data-personalization-container]"
    );
    const section = personalizationContainer?.closest(".shopify-section");
    const formInSection = section?.querySelector(
      ".js-product-form.js-product-form-main"
    );

    if (formInSection) return formInSection;

    return (
      document.querySelector(".js-product-form.js-product-form-main") ||
      document.querySelector(".js-product-form")
    );
  }

  /**
   * Genera un PID único
   */
  generatePid() {
    return Math.floor(
      Math.random() * Math.floor(Number.MAX_SAFE_INTEGER)
    ).toString();
  }

  /**
   * Crea los inputs de addon dinámicamente DENTRO del formulario
   */
  createAddonInputs() {
    // Buscar todos los contenedores de datos de addon
    const addonDataContainers = document.querySelectorAll(
      ".customization-addon-data"
    );

    if (addonDataContainers.length === 0) {
      return;
    }

    this.form
      .querySelectorAll(".customization-addon-input, .customization-base-input")
      .forEach((input) => input.remove());
    this.parentIdInputs = [];
    this.addonInputsByIndex.clear();
    this.addonMetaByIndex.clear();
    this.basePidInput = null;
    this.baseCustomizedInput = null;
    this.baseLinkedItemInput = null;
    this.sharedPid = null;

    this.createBaseInputs();

    addonDataContainers.forEach((container) => {
      const addonIndex = container.dataset.addonIndex;
      const addonVariantId = container.dataset.addonVariantId;
      const currentVariantId = container.dataset.currentVariantId;
      const addonName = container.dataset.addonName;
      this.addonMetaByIndex.set(addonIndex, { addonVariantId, addonName });

      // Crear array para almacenar los inputs de este addon
      const inputs = [];

      // Input: items[N][id]
      const idInput = document.createElement("input");
      idInput.type = "hidden";
      idInput.name = "";
      idInput.value = addonVariantId;
      idInput.className = "customization-addon-input";
      idInput.dataset.addonIndex = addonIndex;
      // Mantener formato legacy porque el parser actual del carrito lo espera asi.
      idInput.dataset.name = `items[${addonIndex}]id`;
      this.form.appendChild(idInput);
      inputs.push(idInput);

      // Input: items[N][quantity]
      const quantityInput = document.createElement("input");
      quantityInput.type = "hidden";
      quantityInput.name = "";
      quantityInput.value = "1";
      quantityInput.className = "customization-addon-input";
      quantityInput.dataset.addonIndex = addonIndex;
      // Mantener formato legacy porque el parser actual del carrito lo espera asi.
      quantityInput.dataset.name = `items[${addonIndex}]quantity`;
      this.form.appendChild(quantityInput);
      inputs.push(quantityInput);

      // Input: items[N][parent_id]
      const parentIdInput = document.createElement("input");
      parentIdInput.type = "hidden";
      parentIdInput.name = "";
      parentIdInput.value = currentVariantId;
      parentIdInput.className =
        "customization-addon-input customization-parent-id";
      parentIdInput.dataset.addonIndex = addonIndex;
      // Mantener formato legacy porque el parser actual del carrito lo espera asi.
      parentIdInput.dataset.name = `items[${addonIndex}]parent_id`;
      this.form.appendChild(parentIdInput);
      inputs.push(parentIdInput);
      this.parentIdInputs.push(parentIdInput);

      // Input: items[N][properties][Nombre]
      if (addonName) {
        const propertyInput = document.createElement("input");
        propertyInput.type = "hidden";
        propertyInput.name = "";
        propertyInput.value = "";
        propertyInput.className =
          "customization-addon-input customization-addon-property";
        propertyInput.dataset.addonIndex = addonIndex;
        propertyInput.dataset.propertyName = addonName;
        // Mantener formato legacy porque el parser actual del carrito lo espera asi.
        propertyInput.dataset.name = `items[${addonIndex}]properties[${addonName}]`;
        this.form.appendChild(propertyInput);
        inputs.push(propertyInput);
      }

      // Input: properties[Nombre] en el producto base - mismo valor del addon de texto.
      if (addonName) {
        const mainTextPropertyInput = document.createElement("input");
        mainTextPropertyInput.type = "hidden";
        mainTextPropertyInput.name = "";
        mainTextPropertyInput.value = "";
        mainTextPropertyInput.className =
          "customization-addon-input customization-main-text-property";
        mainTextPropertyInput.dataset.addonIndex = addonIndex;
        mainTextPropertyInput.dataset.name = `properties[${addonName}]`;
        this.form.appendChild(mainTextPropertyInput);
        inputs.push(mainTextPropertyInput);
      }

      // ========== PROPERTIES PARA EL PRODUCTO PERSONALIZABLE (items[N]) ==========

      // Input: items[N][properties][_pid]
      const addonPidInput = document.createElement("input");
      addonPidInput.type = "hidden";
      addonPidInput.name = "";
      addonPidInput.value = "";
      addonPidInput.className =
        "customization-addon-input customization-addon-pid";
      addonPidInput.dataset.addonIndex = addonIndex;
      // Mantener formato legacy porque el parser actual del carrito lo espera asi.
      addonPidInput.dataset.name = `items[${addonIndex}]properties[_pid]`;
      this.form.appendChild(addonPidInput);
      inputs.push(addonPidInput);

      // Input: items[N][properties][customized]
      const addonCustomizedInput = document.createElement("input");
      addonCustomizedInput.type = "hidden";
      addonCustomizedInput.name = "";
      addonCustomizedInput.value = "true";
      addonCustomizedInput.className =
        "customization-addon-input customization-addon-customized";
      addonCustomizedInput.dataset.addonIndex = addonIndex;
      // Mantener formato legacy porque el parser actual del carrito lo espera asi.
      addonCustomizedInput.dataset.name = `items[${addonIndex}]properties[customized]`;
      this.form.appendChild(addonCustomizedInput);
      inputs.push(addonCustomizedInput);

      // Input: items[N][properties][_hidden] - Para ocultar en el carrito
      const addonHiddenInput = document.createElement("input");
      addonHiddenInput.type = "hidden";
      addonHiddenInput.name = "";
      addonHiddenInput.value = "true";
      addonHiddenInput.className =
        "customization-addon-input customization-addon-hidden";
      addonHiddenInput.dataset.addonIndex = addonIndex;
      // Mantener formato legacy porque el parser actual del carrito lo espera asi.
      addonHiddenInput.dataset.name = `items[${addonIndex}]properties[_hidden]`;
      this.form.appendChild(addonHiddenInput);
      inputs.push(addonHiddenInput);

      // Guardar todos los inputs de este addon
      this.addonInputsByIndex.set(addonIndex, inputs);
    });
  }

  /**
   * Crea inputs compartidos de properties para el producto base.
   */
  createBaseInputs() {
    const existingPidInput = this.form.querySelector(".customization-base-pid");
    const existingCustomizedInput = this.form.querySelector(
      ".customization-base-customized"
    );
    const existingLinkedInput = this.form.querySelector(
      ".customization-base-linked-item"
    );

    if (existingPidInput && existingCustomizedInput && existingLinkedInput) {
      this.basePidInput = existingPidInput;
      this.baseCustomizedInput = existingCustomizedInput;
      this.baseLinkedItemInput = existingLinkedInput;
      return;
    }

    const basePidInput = document.createElement("input");
    basePidInput.type = "hidden";
    basePidInput.name = "";
    basePidInput.value = "";
    basePidInput.className = "customization-base-input customization-base-pid";
    basePidInput.dataset.name = "properties[_pid]";
    this.form.appendChild(basePidInput);
    this.basePidInput = basePidInput;

    const baseCustomizedInput = document.createElement("input");
    baseCustomizedInput.type = "hidden";
    baseCustomizedInput.name = "";
    baseCustomizedInput.value = "true";
    baseCustomizedInput.className =
      "customization-base-input customization-base-customized";
    baseCustomizedInput.dataset.name = "properties[customized]";
    this.form.appendChild(baseCustomizedInput);
    this.baseCustomizedInput = baseCustomizedInput;

    const baseLinkedItemInput = document.createElement("input");
    baseLinkedItemInput.type = "hidden";
    baseLinkedItemInput.name = "";
    baseLinkedItemInput.value = "";
    baseLinkedItemInput.className =
      "customization-base-input customization-base-linked-item";
    baseLinkedItemInput.dataset.name = "properties[_linkedItem]";
    this.form.appendChild(baseLinkedItemInput);
    this.baseLinkedItemInput = baseLinkedItemInput;
  }

  /**
   * Devuelve los addonIndex activados actualmente.
   */
  getEnabledAddonIndexes() {
    return Array.from(this.togglesByIndex.entries())
      .filter(([, toggle]) => toggle.checked)
      .map(([addonIndex]) => addonIndex);
  }

  /**
   * Sincroniza los properties del producto base y el PID compartido.
   */
  syncBaseInputs() {
    const enabledAddonIndexes = this.getEnabledAddonIndexes();
    const hasEnabledAddons = enabledAddonIndexes.length > 0;

    if (!hasEnabledAddons) {
      this.sharedPid = null;

      [this.basePidInput, this.baseCustomizedInput, this.baseLinkedItemInput].forEach(
        (input) => {
          if (!input) return;
          input.name = "";
          input.value =
            input === this.baseCustomizedInput ? "true" : "";
        }
      );
      return;
    }

    if (!this.sharedPid) {
      this.sharedPid = this.generatePid();
    }

    if (this.basePidInput) {
      this.basePidInput.name = this.basePidInput.dataset.name;
      this.basePidInput.value = this.sharedPid;
    }

    if (this.baseCustomizedInput) {
      this.baseCustomizedInput.name = this.baseCustomizedInput.dataset.name;
      this.baseCustomizedInput.value = "true";
    }

    if (this.baseLinkedItemInput) {
      const firstEnabledAddon = enabledAddonIndexes
        .map((addonIndex) => this.addonMetaByIndex.get(addonIndex))
        .find(Boolean);

      this.baseLinkedItemInput.name = this.baseLinkedItemInput.dataset.name;
      this.baseLinkedItemInput.value =
        firstEnabledAddon?.addonVariantId || "";
    }

    enabledAddonIndexes.forEach((addonIndex) => {
      const inputs = this.addonInputsByIndex.get(addonIndex);
      if (!inputs) return;

      const addonPidInput = inputs.find((input) =>
        input.classList.contains("customization-addon-pid")
      );
      if (addonPidInput) {
        addonPidInput.value = this.sharedPid;
      }
    });
  }

  /**
   * Maneja el cambio del checkbox de personalización
   */
  handleToggleChange(event) {
    const toggle = event.target;
    const addonIndex = toggle.dataset.addonIndex;
    const isChecked = toggle.checked;

    if (isChecked) {
      this.enableAddonInputs(addonIndex);
      this.showTextInput(addonIndex);
    } else {
      this.disableAddonInputs(addonIndex);
      this.hideTextInput(addonIndex);
    }
  }

  /**
   * Habilita todos los inputs del addon
   * Genera un PID único cuando se activa
   */
  enableAddonInputs(addonIndex) {
    const inputs = this.addonInputsByIndex.get(addonIndex);
    if (!inputs) return;

    inputs.forEach((input) => {
      const originalName = input.dataset.name;
      if (originalName) {
        input.name = originalName;
      }
    });
    this.syncBaseInputs();
  }

  /**
   * Deshabilita todos los inputs del addon
   */
  disableAddonInputs(addonIndex) {
    const inputs = this.addonInputsByIndex.get(addonIndex);
    if (!inputs) return;

    inputs.forEach((input) => {
      input.name = "";
      // Limpiar valores
      if (
        input.classList.contains("customization-addon-pid") ||
        input.classList.contains("customization-addon-property") ||
        input.classList.contains("customization-main-text-property")
      ) {
        input.value = "";
      }
    });

    // Limpiar el texto si existe
    const textInput = this.textInputsByIndex.get(addonIndex);
    if (textInput) {
      textInput.value = "";
    }
    this.syncBaseInputs();
  }

  /**
   * Muestra el input de texto
   */
  showTextInput(addonIndex) {
    const textInput = this.textInputsByIndex.get(addonIndex);
    if (!textInput) return;

    const container = textInput.closest('[id$="_text_container"]');
    if (container) {
      container.classList.remove("hidden");
    }
  }

  /**
   * Oculta el input de texto
   */
  hideTextInput(addonIndex) {
    const textInput = this.textInputsByIndex.get(addonIndex);
    if (!textInput) return;

    const container = textInput.closest('[id$="_text_container"]');
    if (container) {
      container.classList.add("hidden");
    }
  }

  /**
   * Maneja el cambio del input de texto
   * Sincroniza el valor con los inputs hidden de properties
   */
  handleTextInputChange(event) {
    const textInput = event.target;
    const addonIndex = textInput.dataset.addonIndex;
    const value = textInput.value;

    // Encontrar los inputs de property correspondientes
    const inputs = this.addonInputsByIndex.get(addonIndex);
    if (!inputs) return;

    // Sincronizar con items[N][properties][Nombre] (addon)
    const propertyInput = inputs.find((i) =>
      i.classList.contains("customization-addon-property")
    );
    if (propertyInput) {
      propertyInput.value = value;
    }

    // Sincronizar con properties[Nombre] (producto principal)
    const mainTextPropertyInput = inputs.find((i) =>
      i.classList.contains("customization-main-text-property")
    );
    if (mainTextPropertyInput) {
      mainTextPropertyInput.value = value;
    }
  }

  /**
   * Actualiza todos los parent_id con el variant ID actual
   */
  updateAllParentIds() {
    const currentVariantId = this.variantIdInput.value;

    this.parentIdInputs.forEach((input) => {
      input.value = currentVariantId;
    });
  }
}

// Inicializar cuando el script se carga
window.customizationHandler = new ProductCustomizationFormHandler();
